import { GetLimitConfiguration } from '@staycool/bets-types/limit-configuration';
import { getServiceUrl, httpGet, httpPost } from '../services/api';
import { getActiveCurrency } from '../services/currency';
import { logger } from '../services/logger';
import { CASHOUT_NOT_ELIGIBLE_ERROR } from '../services/sports/constants';
import { Ticket } from '../services/sports/types';
import { convertArrayToObject } from '../services/util';
import { BetslipShareResponse, DatabaseBetslipShare } from '@staycool/bets-types/dist/_features/betslip-share/types';

const getUrl = (url) => getServiceUrl('bets', url);

export async function placeBetRequest(data) {
    const url = getUrl('bets');
    return httpPost<string>(url, data);
}

export async function getLatestTicketId(timestampFrom) {
    const currentFoTime = new Date().getTime();
    const url = getUrl('tickets/latest');
    return httpGet<string>(url, { timestampFrom, currentFoTime });
}

export async function placeFreeBetRequest(body) {
    const url = getUrl('bets/free');
    return httpPost<string>(url, body);
}

export async function placeFreeBetV2Request(body) {
    const url = getUrl('bets/free-v2');
    return httpPost<string>(url, body);
}

export async function placeRiskFreeBetRequest(body) {
    const url = getUrl('bets/risk-free');
    return httpPost<string>(url, body);
}

export async function placeMultiplierBetRequest(body) {
    const url = getUrl('bets/multiplier');
    return httpPost<string>(url, body);
}

export async function getMatchTurnover(marketId, currency = getActiveCurrency()) {
    const turnoverStats = await httpGet<
        { id: number; market_id: number; stake: number; bets: number; share: number; sum: number }[]
    >(getUrl(`stats/market`), {
        id: marketId,
        currency,
    });
    return convertArrayToObject(turnoverStats, 'id');
}

export function doCashout(ticketId, cashoutAmount, acceptAnyOddsChanges, acceptLimitedOddsChanges) {
    return httpPost<{ amount: number; amount_uc: number; id: string }[]>(getUrl(`cashout/${ticketId}`), {
        cashoutAmount,
        acceptAnyOddsChanges,
        acceptLimitedOddsChanges,
    });
}

export async function isBetslipCashoutEligible(ticketType, marketIds) {
    if (!marketIds || !marketIds.length) {
        return true;
    }
    try {
        await httpGet(getUrl('cashout/is-betslip-eligible/'), { marketIds, ticket_type: ticketType });
        return true;
    } catch (e: any) {
        if ([CASHOUT_NOT_ELIGIBLE_ERROR, 1152, 1154, 1155].includes(e.code)) {
            return false;
        }
        return true;
    }
}

export type IsSameMatchComboEligiblePayload = {
    matchId: number;
    totalsMarketId: number;
    handicapMarketId?: number;
}[];

export async function isSameMatchComboEligible(payload: IsSameMatchComboEligiblePayload) {
    if (!payload.length) {
        return {};
    }
    try {
        return await httpPost<Record<number, boolean>>(getUrl('validation/is-same-match-combo-eligible'), payload);
    } catch (e) {
        logger.error('BetsMicroservice', 'isSameMatchComboEligible', e);
        return {};
    }
}

export async function isSameMatchComboEligibleFromSets(payload: Record<number, number[]>) {
    try {
        return await httpPost<Record<number, boolean>>(getUrl('validation/is-same-match-combo-eligible-v2'), payload);
    } catch (e) {
        logger.error('BetsMicroservice', 'isSameMatchComboEligibleFromSets', e);
        return {};
    }
}

export async function getLimitsByCurrency(currency: string) {
    try {
        const url = getUrl(`limit-configuration/${currency}`);
        const response = await httpGet<GetLimitConfiguration | null>(url);
        if (!response) {
            throw new Error(`Limits for ${currency} not found`);
        }
        return response;
    } catch (err) {
        logger.error('BetsMicroservice', 'getLimitsByCurrency', err);
        return {} as GetLimitConfiguration;
    }
}

export function fetchCashouts(offsetDate) {
    return httpGet<{ hasMore: boolean; tickets: Ticket[] }>(getUrl('cashout/eligible-tickets'), { offsetDate });
}

export async function getSharedBetslipInfo(betslipCode: string) {
    try {
        const url = getUrl(`betslip-share/${betslipCode}`);
        return await httpGet<BetslipShareResponse>(url);
    } catch (err) {
        logger.error('BetsMicroservice', 'getSharedBetslipInfo', err);
    }
}

export async function shareBetslip(outcomeIds: number[], betType: string, comboCardId?: number) {
    const url = getUrl('betslip-share');
    return await httpPost<DatabaseBetslipShare>(url, { outcomeIds, betType, comboCardId });
}
